import { makeAutoObservable, observable, runInAction, toJS } from 'mobx'
import RawReportFilters from '../data/ReportFilters'
import { fetchChartById, fetchLiveChartById } from '@services/api/chartAPI'
import { convertToHighcharts } from '@utils/ChartHelper'
import rootStore from './RootStore'
import { toUpper } from 'lodash'

const chartRequestContext = () => ({
  reportId: rootStore.reportStore.reportId,
  snapshotId: rootStore.reportStore.currentSnapshotId,
})

const isStaleChartResponse = requestContext =>
  rootStore.reportStore.reportId !== requestContext.reportId ||
  rootStore.reportStore.currentSnapshotId !== requestContext.snapshotId

class ChartStore {
  charts = {}
  downloadSelection = []
  highchartOptions = {}
  loadingChart = false
  error = null
  filters = []
  allFilters = []
  reportStore = null

  /**
   * Live Highcharts wrappers by chart id. A mounted instance is not report data: it must not live in
   * `charts`, where clearing the dictionary for a new view left the write order-dependent, and where
   * MobX deep-proxied a Highcharts wrapper.
   */
  chartInstances = new Map()

  constructor(rootStore) {
    makeAutoObservable(this, { chartInstances: false })

    this.loadFilters()

    this.reportStore = rootStore.reportStore
  }

  setHighchartOption(value) {
    this.highchartOptions = value
  }

  get downloadSelectionCount() {
    return this.downloadSelection
  }

  loadFilters = () => {
    try {
      this.allFilters = RawReportFilters
    } catch (e) {
      this.error = e
    }
  }


  setCharts = charts => {
    this.charts = charts
  }

  registerChartInstance = (chartId, instance) => {
    this.chartInstances.set(chartId, instance)
  }

  unregisterChartInstance = chartId => {
    this.chartInstances.delete(chartId)
  }

  getChartInstance = chartId => this.chartInstances.get(chartId)

  fetchChartById = async ({ reportId, chartId }) => {
    try {
      this.loadingChart = true

      const {
        productsStore: { currentProduct },
        planStore: { currentPlan },
      } = rootStore

      const requestContext = chartRequestContext()

      let remoteChartData
      if (currentProduct === 'pa' && toUpper(reportId) === 'LIVEREPORT') {
        remoteChartData = await fetchLiveChartById({
          planId: currentPlan?.planId,
          productId: currentProduct, // TODO change this to currentProduct
          componentId: chartId,
          snapshotId: requestContext.snapshotId,
        })
      } else {
        remoteChartData = await fetchChartById({
          reportId,
          componentId: chartId,
        })
      }

      const chart = remoteChartData?.component

      runInAction(() => {
        if (isStaleChartResponse(requestContext)) return
        this.charts[chartId] = {
          id: chartId,
          filters: chart.filters,
          viewType: chart.viewType,
          header: chart.header.value,
          subHeader: chart.subHeader?.value || '', // Add subHeader
          caption: {
            title: chart?.caption?.title || 'Notes:',
            text: chart?.caption?.body || 'There is no notes',
          },
          chartOptions: convertToHighcharts(chart),
          tooltipHeader: chart.headerFootnote,
          title: chart?.caption?.title || chart.title,
          text: chart?.caption?.body,
          drillDownInfo: chart.drillDownInfo,
        }
      })
    } catch (e) {
      this.error = 'An error happened while tried to fetch chart'
    } finally {
      this.loadingChart = false
    }
  }

  /**
   * Fetch multiple charts in parallel to reduce waterfall latency
   * @param {string} reportId - The report ID
   * @param {Array<string>} chartIds - Array of chart IDs to fetch
   */
  fetchMultipleCharts = async ({ reportId, chartIds }) => {
    try {
      this.loadingChart = true

      const {
        productsStore: { currentProduct },
        planStore: { currentPlan },
      } = rootStore

      const requestContext = chartRequestContext()

      // Fetch all charts in parallel
      const chartPromises = chartIds.map(chartId => {
        if (currentProduct === 'pa' && toUpper(reportId) === 'LIVEREPORT') {
          return fetchLiveChartById({
            planId: currentPlan?.planId,
            productId: currentProduct,
            componentId: chartId,
            snapshotId: requestContext.snapshotId,
          }).then(data => ({ chartId, data }))
        } else {
          return fetchChartById({
            reportId,
            componentId: chartId,
          }).then(data => ({ chartId, data }))
        }
      })

      const results = await Promise.all(chartPromises)

      runInAction(() => {
        if (isStaleChartResponse(requestContext)) return
        results.forEach(({ chartId, data }) => {
          const chart = data?.component
          if (chart) {
            this.charts[chartId] = {
              id: chartId,
              filters: chart.filters,
              viewType: chart.viewType,
              header: chart.header.value,
              subHeader: chart.subHeader?.value || '',
              caption: {
                title: chart?.caption?.title || 'Notes:',
                text: chart?.caption?.body || 'There is no notes',
              },
              chartOptions: convertToHighcharts(chart),
              tooltipHeader: chart.headerFootnote,
              title: chart?.caption?.title || chart.title,
              text: chart?.caption?.body,
              drillDownInfo: chart.drillDownInfo,
            }
          }
        })
      })
    } catch (e) {
      this.error = 'An error happened while trying to fetch charts'
    } finally {
      this.loadingChart = false
    }
  }
}
export default ChartStore
